import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { z } from 'zod'
import { HttpError } from '@/api/http'
import { trackNewsletterSubscribed } from '@/lib/track'
import { loadStoredEmail, saveStoredEmail } from '@/lib/userEmail'
import { subscribeToNewsletter } from './subscribeToNewsletter'

export type NewsletterStatus = 'idle' | 'submitting' | 'success' | 'invalid-email' | 'error'

interface UseNewsletterForm {
  email: string
  setEmail: (value: string) => void
  status: NewsletterStatus
  submit: (event: React.SubmitEvent<HTMLFormElement>) => void
}

export function useNewsletterForm(onSuccess?: () => void): UseNewsletterForm {
  const [email, setEmail] = useState(loadStoredEmail)
  const [status, setStatus] = useState<NewsletterStatus>('idle')

  const subscribe = async (trimmedEmail: string): Promise<void> => {
    saveStoredEmail(trimmedEmail)
    setStatus('submitting')
    try {
      await subscribeToNewsletter(trimmedEmail)
      void trackNewsletterSubscribed(trimmedEmail)
      setStatus('success')
      onSuccess?.()
    } catch (error) {
      // The backend answers 400 when the address is not a real deliverable
      // e-mail (typo in the domain) — that is a user error, not an incident.
      if (error instanceof HttpError && error.status === 400) {
        setStatus('invalid-email')
        return
      }

      Sentry.captureException(error)
      setStatus('error')
    }
  }

  const submit = (event: React.SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (status === 'submitting') return

    // Native validation can be bypassed (autofill, older browsers), so guard
    // here — an empty field or a typo must not reach the API to come back 400.
    const trimmedEmail = email.trim()
    if (!z.email().safeParse(trimmedEmail).success) {
      setStatus('invalid-email')
      return
    }

    // subscribe() handles every failure itself (status + Sentry).
    void subscribe(trimmedEmail)
  }

  return { email, setEmail, status, submit }
}
